import { Link } from "react-router-dom";
import { Boxes, Package, Beaker, Clock, TrendingUp } from "lucide-react";

import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { InternalMetricCard } from "@/components/internal/shared/InternalMetricCard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useProducts } from "@/hooks/useProducts";
import { useCargos } from "@/hooks/useCargos";
import { useAppLocale } from "@/lib/i18n";
import { hasPermission } from "@/lib/roles";
import { m } from "@/paraglide/messages.js";

export default function InventoryDashboardPage() {
  useAppLocale();
  const { userRole } = useAuthIdentity();
  const products = useProducts();
  const cargos = useCargos(1000);
  const canViewSamples = hasPermission(userRole, "samples:view");

  const productCount = products?.length ?? 0;
  const activeCargos = cargos?.filter((c: any) => c.status !== "archived" && c.status !== "sold_out") ?? [];
  const activeCargosCount = activeCargos.length;
  const totalBags = activeCargos.reduce((sum: number, c: any) => sum + (c.availableBags || 0), 0);
  const warehouseBags = activeCargos.reduce((sum: number, c: any) => sum + (c.warehouseBags || 0), 0);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{m.inventory_management_title()}</h1>
        <p className="text-muted-foreground mt-2">{m.inventory_management_description()}</p>
      </div>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <InternalMetricCard title={m.inventory_metric_total_products()} value={String(productCount)} hint={m.inventory_metric_total_products_hint()} icon={Package} />
        <InternalMetricCard title={m.inventory_metric_active_cargos()} value={String(activeCargosCount)} hint={m.inventory_metric_active_cargos_hint()} icon={Boxes} />
        <InternalMetricCard title={m.inventory_metric_total_bags()} value={totalBags.toLocaleString()} hint={m.inventory_metric_total_bags_hint()} icon={Package} />
        <InternalMetricCard title={m.inventory_metric_warehouse_bags()} value={warehouseBags.toLocaleString()} hint={m.inventory_metric_warehouse_bags_hint()} icon={TrendingUp} />
      </section>

      <Card>
        <CardHeader>
          <CardTitle>{m.inventory_quick_navigation_title()}</CardTitle>
          <CardDescription>{m.inventory_quick_navigation_description()}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <NavCard to="/inventory/products" icon={Package} title={m.nav_products()} description={m.inventory_nav_products_description()} />
            <NavCard to="/inventory/cargos" icon={Boxes} title={m.nav_cargos()} description={m.inventory_nav_cargos_description()} />
            {canViewSamples ? (
              <NavCard to="/inventory/samples" icon={Beaker} title={m.nav_samples()} description={m.inventory_nav_samples_description()} />
            ) : null}
            <NavCard to="/inventory/reservations" icon={Clock} title={m.nav_reservations()} description={m.inventory_nav_reservations_description()} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function NavCard({ to, icon: Icon, title, description }: { to: string; icon: React.ComponentType<{ className?: string }>; title: string; description: string }) {
  return (
    <Link to={to} className="cursor-pointer rounded-lg border border-muted p-4 transition-colors hover:border-primary hover:bg-muted/50">
      <h3 className="mb-1 flex items-center gap-2 font-semibold">
        <Icon className="h-4 w-4" />
        {title}
      </h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </Link>
  );
}
